import React, { useState, useEffect } from 'react';
import './Navbar.css';
import logoPHSchool from '../assets/logo/logo_PHSchool.png';

export default function Navbar({
  headerMenu = [],
  activeItemId,
  activeSlug,
  onSelectSlug,
  goToSlug,
  goToHomepage,
  getSubmenuItemsForNavItem,
  goToSection,
  loadingMenu
}) {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileMenuId, setExpandedMobileMenuId] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Track window scroll to add elevation
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Body scroll locking when mobile menu drawer is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  // Handle menu click - scroll to top
  const handleMenuClick = (item) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onSelectSlug(item);
    setIsMobileMenuOpen(false);
    setExpandedMobileMenuId(null);
  };

  // Handle submenu click - scroll to section top
  const handleSubmenuClick = (child) => {
    if ('anchor' in child) {
      goToSection(child.slug, child.anchor);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      onSelectSlug(child);
    }
    setIsMobileMenuOpen(false);
    setExpandedMobileMenuId(null);
  };

  // Toggle mobile accordion
  const toggleMobileAccordion = (e, itemId) => {
    e.stopPropagation();
    setExpandedMobileMenuId((prev) => (prev === itemId ? null : itemId));
  };

  const isContactActive = activeSlug === 'contact-us';

  // Filter out 'homepage' and 'contact-us' from header items
  // Contact Us is rendered as a dedicated button on the right
  const menuItems = (!loadingMenu && headerMenu)
    ? headerMenu.filter((item) => item.slug !== 'homepage' && item.slug !== 'contact-us')
    : [];

  return (
    <>
      <header className={`navbar-header ${isScrolled ? 'is-scrolled' : ''}`}>
        <nav className="navbar" aria-label="Main Navigation">
          {/* Logo */}
          <button 
            className="navbar-logo" 
            onClick={() => {
              goToHomepage();
              setIsMobileMenuOpen(false);
            }} 
            type="button"
            aria-label="Permata Harapan Secondary Homepage"
          >
            <img src={logoPHSchool} alt="Permata Harapan Secondary" />
          </button>

          {/* Desktop Menu */}
          <ul className="navbar-menu">
            {menuItems.map((item) => {
              const submenuItems = getSubmenuItemsForNavItem(item) || [];
              const hasSubmenu = submenuItems.length > 0;
              const isItemActive = (activeItemId && activeItemId === item.id) || (activeSlug && activeSlug === item.slug);

              return (
                <li
                  key={item.id || item.slug}
                  className="navbar-menu-item"
                  onMouseEnter={() => setOpenMenuId(item.id || item.slug)}
                  onMouseLeave={() => setOpenMenuId(null)}
                >
                  <button
                    className={`navbar-menu-link ${isItemActive ? 'active' : ''}`}
                    onClick={() => handleMenuClick(item)}
                    type="button"
                    aria-expanded={hasSubmenu ? openMenuId === (item.id || item.slug) : undefined}
                    aria-haspopup={hasSubmenu ? 'true' : undefined}
                  >
                    <span>{item.title}</span>
                    {hasSubmenu && (
                      <svg 
                        className={`navbar-dropdown-chevron ${openMenuId === (item.id || item.slug) ? 'open' : ''}`}
                        viewBox="0 0 20 20" 
                        fill="currentColor" 
                        width="14" 
                        height="14"
                        aria-hidden="true"
                      >
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>

                  {hasSubmenu && (
                    <ul 
                      className={`navbar-submenu ${openMenuId === (item.id || item.slug) ? 'open' : ''}`}
                      role="menu"
                    >
                      {submenuItems.map((child, idx) => (
                        <li key={child.id || child.title || idx} role="none">
                          <button
                            className={`navbar-submenu-link ${
                              activeItemId === child.id ? 'active' : ''
                            }`}
                            onClick={() => handleSubmenuClick(child)}
                            type="button"
                            role="menuitem"
                          >
                            <span className="navbar-submenu-bullet" aria-hidden="true"></span>
                            <span>{child.title}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Right Actions */}
          <div className="navbar-actions">
            <button 
              className={`navbar-btn-contact ${isContactActive ? 'active' : ''}`} 
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                goToSlug('contact-us');
              }}
              type="button"
              aria-label="Navigate to Contact Us"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <span>Contact Us</span>
            </button>

            {/* Hamburger Button for Tablet & Mobile */}
            <button
              className={`navbar-toggle ${isMobileMenuOpen ? 'open' : ''}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              type="button"
              aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMobileMenuOpen}
            >
              <span className="navbar-toggle-bar"></span>
              <span className="navbar-toggle-bar"></span>
              <span className="navbar-toggle-bar"></span>
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile / Tablet Drawer & Backdrop */}
      <div 
        className={`navbar-mobile-backdrop ${isMobileMenuOpen ? 'open' : ''}`} 
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      />

      <aside 
        className={`navbar-mobile-panel ${isMobileMenuOpen ? 'open' : ''}`}
        aria-label="Mobile Navigation Drawer"
        aria-hidden={!isMobileMenuOpen}
      >
        {/* Drawer Header */}
        <div className="navbar-mobile-header">
          <div className="navbar-mobile-brand">
            <img src={logoPHSchool} alt="PHS" className="navbar-mobile-logo" />
            <div className="navbar-mobile-brand-text">
              <span className="navbar-mobile-school-name">Permata Harapan</span>
              <span className="navbar-mobile-school-sub">Secondary School</span>
            </div>
          </div>
          <button 
            className="navbar-mobile-close-btn"
            onClick={() => setIsMobileMenuOpen(false)}
            type="button"
            aria-label="Close navigation menu"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Scrollable Navigation List */}
        <div className="navbar-mobile-body">
          <ul className="navbar-mobile-list">
            {menuItems.map((item) => {
              const submenuItems = getSubmenuItemsForNavItem(item) || [];
              const hasSubmenu = submenuItems.length > 0;
              const isExpanded = expandedMobileMenuId === (item.id || item.slug);
              const isItemActive = (activeItemId && activeItemId === item.id) || (activeSlug && activeSlug === item.slug);

              return (
                <li key={item.id || item.slug} className={`navbar-mobile-item ${isExpanded ? 'expanded' : ''}`}>
                  <div className={`navbar-mobile-item-row ${isItemActive ? 'active' : ''}`}>
                    <button
                      className="navbar-mobile-link"
                      onClick={() => handleMenuClick(item)}
                      type="button"
                    >
                      <span className="navbar-mobile-bullet"></span>
                      <span className="navbar-mobile-link-text">{item.title}</span>
                      {item.slug === 'contact-us' && (
                        <span className="navbar-mobile-tag">Active</span>
                      )}
                    </button>

                    {hasSubmenu && (
                      <button
                        className={`navbar-mobile-chevron-btn ${isExpanded ? 'expanded' : ''}`}
                        onClick={(e) => toggleMobileAccordion(e, item.id || item.slug)}
                        type="button"
                        aria-label={`Toggle ${item.title} submenu`}
                        aria-expanded={isExpanded}
                      >
                        <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {hasSubmenu && isExpanded && (
                    <ul className="navbar-mobile-submenu">
                      {submenuItems.map((child, cIdx) => (
                        <li key={child.id || child.title || cIdx}>
                          <button
                            className={`navbar-mobile-sublink ${activeItemId === child.id ? 'active' : ''}`}
                            onClick={() => handleSubmenuClick(child)}
                            type="button"
                          >
                            <span className="navbar-mobile-subbullet"></span>
                            <span>{child.title}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Drawer Footer Actions */}
        <div className="navbar-mobile-footer">
          <button
            className="navbar-mobile-cta-btn"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              goToSlug('contact-us');
              setIsMobileMenuOpen(false);
            }}
            type="button"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <span>Contact Us Now</span>
          </button>
          
          <div className="navbar-mobile-meta">
            <span className="navbar-mobile-meta-item">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              <span>+62 815 1759 3512</span>
            </span>
            <span className="navbar-mobile-meta-item">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
              <span>permataharapansecondary.sch.id</span>
            </span>
          </div>
        </div>
      </aside>
    </>
  );
}
